import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { rateLookup, getMyRates } from "../api/rates";

// Rates are per-minute and small; show enough precision to be meaningful.
function fmtRate(r: number | null): string {
  if (r == null) return "—";
  return `$${r.toFixed(5)}/min`;
}
function fmtMoney(n: number): string {
  return `$${n.toFixed(2)}`;
}
function fmtNpaNxx(s: string): string {
  return s.length === 6 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function RateLookup() {
  const [q, setQ] = useState("");
  const debounced = useDebounced(q, 300);
  const digits = debounced.replace(/\D/g, "");
  const { data, isFetching } = useQuery({
    queryKey: ["rate-lookup", digits],
    queryFn: () => rateLookup(digits),
    enabled: digits.length >= 3,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h2 className="text-sm font-semibold mb-2">Look up a rate</h2>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Area code (832) or full prefix (832201)"
        className="w-full sm:w-80 border border-gray-300 rounded px-3 py-2 text-sm"
        inputMode="numeric"
      />
      <div className="mt-3">
        {digits.length > 0 && digits.length < 3 && (
          <p className="text-xs text-gray-400">Enter at least a 3-digit area code.</p>
        )}
        {digits.length >= 3 && isFetching && <p className="text-xs text-gray-400">Searching…</p>}
        {data && data.rows.length === 0 && !isFetching && (
          <p className="text-sm text-gray-500">No rate found for “{digits}”.</p>
        )}
        {data && data.fallback && (
          <p className="text-xs text-amber-600 mb-1">
            No specific rate on file for this prefix — the default rate applies.
          </p>
        )}
        {data && data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left py-1 pr-4">Prefix</th>
                  <th className="text-left py-1 pr-4">State</th>
                  <th className="text-right py-1 pr-4">Interstate</th>
                  <th className="text-right py-1">Intrastate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r) => (
                  <tr key={r.npa_nxx}>
                    <td className="py-1 pr-4 font-mono">{fmtNpaNxx(r.npa_nxx)}</td>
                    <td className="py-1 pr-4 text-gray-600">{r.state || "—"}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{fmtRate(r.interstate_rate)}</td>
                    <td className="py-1 text-right tabular-nums">{fmtRate(r.intrastate_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MyDestinations() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-rates"],
    queryFn: () => getMyRates(50, 30),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h2 className="text-sm font-semibold">Your top destinations</h2>
        {data && (
          <span className="text-xs text-gray-500">
            last {data.days} days · {data.totals.calls.toLocaleString()} calls ·{" "}
            {data.totals.distinct_npa_nxx.toLocaleString()} prefixes · {fmtMoney(data.totals.spend)}
          </span>
        )}
      </div>
      {isLoading && <p className="text-sm text-gray-400">Loading your destinations…</p>}
      {isError && <p className="text-sm text-red-600">Couldn’t load your destinations. Try again shortly.</p>}
      {data && data.destinations.length === 0 && !isLoading && (
        <p className="text-sm text-gray-500">No answered calls in this window yet.</p>
      )}
      {data && data.destinations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left py-1 pr-4">Prefix</th>
                <th className="text-left py-1 pr-4">State</th>
                <th className="text-right py-1 pr-4">Calls</th>
                <th className="text-right py-1 pr-4">Interstate</th>
                <th className="text-right py-1 pr-4">Intrastate</th>
                <th className="text-right py-1">Spend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.destinations.map((r) => (
                <tr key={r.npa_nxx}>
                  <td className="py-1 pr-4 font-mono">{fmtNpaNxx(r.npa_nxx)}</td>
                  <td className="py-1 pr-4 text-gray-600">{r.state || "—"}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{r.calls.toLocaleString()}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{fmtRate(r.interstate_rate)}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{fmtRate(r.intrastate_rate)}</td>
                  <td className="py-1 text-right tabular-nums font-medium">{fmtMoney(r.spend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        You’re billed the interstate or intrastate rate per call, depending on whether your caller ID’s
        state matches the number you’re dialing. Rates are live from the current rate sheet.
      </p>
    </div>
  );
}

export function RatesPage() {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Rates</h1>
      <p className="text-sm text-gray-500 mb-4">
        The per-minute rates you’re billed, by destination prefix (NPA-NXX).
      </p>
      <RateLookup />
      <MyDestinations />
    </div>
  );
}
